import React from 'react';
import Icon from '../../../components/AppIcon';

import { format, parseISO } from 'date-fns';

const CalendarIntegration = ({ bookingData }) => {
  const generateGoogleCalendarUrl = () => {
    try {
      const start = parseISO(bookingData?.scheduledStart);
      const end = parseISO(bookingData?.scheduledEnd);
      
      const startFormatted = format(start, "yyyyMMdd'T'HHmmss");
      const endFormatted = format(end, "yyyyMMdd'T'HHmmss");
      
      const title = encodeURIComponent(bookingData?.title || 'Northstar Analytics Consultation');
      const description = encodeURIComponent(
        `${bookingData?.description || 'Strategic data consultation'}\n\nMeeting Link: Will be sent via email\n\nConsultant: Northstar Analytics Team`
      );
      const location = encodeURIComponent('Virtual Meeting');
      
      return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startFormatted}/${endFormatted}&details=${description}&location=${location}`;
    } catch (err) {
      console.error('Error generating Google Calendar URL:', err);
      return '#';
    }
  };

  const generateOutlookUrl = () => {
    try {
      const start = parseISO(bookingData?.scheduledStart);
      const end = parseISO(bookingData?.scheduledEnd);
      
      const startFormatted = start?.toISOString();
      const endFormatted = end?.toISOString();
      
      const title = encodeURIComponent(bookingData?.title || 'Northstar Analytics Consultation');
      const description = encodeURIComponent(
        `${bookingData?.description || 'Strategic data consultation'}\n\nMeeting Link: Will be sent via email\n\nConsultant: Northstar Analytics Team`
      );
      const location = encodeURIComponent('Virtual Meeting');
      
      return `https://outlook.live.com/calendar/0/deeplink/compose?subject=${title}&startdt=${startFormatted}&enddt=${endFormatted}&body=${description}&location=${location}`;
    } catch (err) {
      console.error('Error generating Outlook URL:', err);
      return '#';
    }
  };

  const generateICalFile = () => {
    try {
      const start = parseISO(bookingData?.scheduledStart);
      const end = parseISO(bookingData?.scheduledEnd);
      
      const startFormatted = format(start, "yyyyMMdd'T'HHmmss");
      const endFormatted = format(end, "yyyyMMdd'T'HHmmss");
      
      const icalContent = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Northstar Analytics//Consultation Booking//EN',
        'BEGIN:VEVENT',
        `UID:${bookingData?.id}@northstaranalytics.eu`,
        `DTSTAMP:${format(new Date(), "yyyyMMdd'T'HHmmss")}`,
        `DTSTART:${startFormatted}`,
        `DTEND:${endFormatted}`,
        `SUMMARY:${bookingData?.title || 'Northstar Analytics Consultation'}`,
        `DESCRIPTION:${bookingData?.description || 'Strategic data consultation'}\\nMeeting Link: Will be sent via email\\nConsultant: Northstar Analytics Team`,
        'LOCATION:Virtual Meeting',
        'STATUS:CONFIRMED',
        'BEGIN:VALARM',
        'TRIGGER:-PT24H',
        'ACTION:DISPLAY',
        'DESCRIPTION:Reminder: Consultation in 24 hours',
        'END:VALARM',
        'BEGIN:VALARM',
        'TRIGGER:-PT1H',
        'ACTION:DISPLAY',
        'DESCRIPTION:Reminder: Consultation in 1 hour',
        'END:VALARM',
        'END:VEVENT',
        'END:VCALENDAR'
      ]?.join('\r\n');
      
      const blob = new Blob([icalContent], { type: 'text/calendar;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'northstar-consultation.ics';
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error generating iCal file:', err);
    }
  };

  const calendarOptions = [
    {
      name: 'Google Calendar',
      icon: 'Calendar',
      color: 'bg-red-500 hover:bg-red-600',
      action: () => window.open(generateGoogleCalendarUrl(), '_blank')
    },
    {
      name: 'Outlook',
      icon: 'Mail',
      color: 'bg-[#002B45] hover:bg-[#003d5c]',
      action: () => window.open(generateOutlookUrl(), '_blank')
    },
    {
      name: 'Apple Calendar',
      icon: 'Apple',
      color: 'bg-gray-800 hover:bg-gray-900',
      action: generateICalFile
    },
    {
      name: 'Other Calendar',
      icon: 'Download',
      color: 'bg-[#1FB5A5] hover:bg-[#1FB5A5]/90',
      action: generateICalFile
    }
  ];

  return (
    <section className="py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-premium p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#002B45] rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="CalendarPlus" size={32} className="text-[#1FB5A5]" />
            </div>
            <h2 className="text-3xl font-bold text-[#002B45] mb-2">
              Add to Your Calendar
            </h2>
            <p className="text-gray-600">
              Never miss your consultation. Add it to your preferred calendar with one click.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {calendarOptions?.map((option) => (
              <button
                key={option?.name}
                onClick={option?.action}
                className={`${option?.color} text-white rounded-xl p-6 transition-all duration-300 hover:shadow-elevation flex flex-col items-center justify-center space-y-3 group`}
              >
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Icon name={option?.icon} size={24} />
                </div>
                <span className="font-semibold text-center">{option?.name}</span>
              </button>
            ))}
          </div>

          <div className="mt-8 p-4 bg-[#E6F0F5] rounded-lg border border-[#002B45]/20">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={20} className="text-[#002B45] mt-0.5 flex-shrink-0" />
              <div className="text-sm text-[#002B45]">
                <p className="font-semibold mb-1">Calendar Invite Includes:</p>
                <ul className="list-disc list-inside space-y-1 text-[#002B45]/80">
                  <li>Meeting date, time, and duration</li>
                  <li>Virtual meeting link (sent via email)</li>
                  <li>Automatic reminders (24 hours and 1 hour before)</li>
                  <li>Consultant information and preparation notes</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CalendarIntegration;